<template>
  <div class="content-grid content-grid--subpage content-grid--subpage-table">
    <Breadcrumbs :title="t(`routes.admin.children.activities.participants.add.name`)"
    />
    <div class="content-grid__header">
      <BackLinkTitle
          :title="t(`routes.admin.children.activities.participants.add.name`)"
      />
      <SearchInput v-if="participants.length"
                   v-model="participantsSearchValue"/>
    </div>
    <Card :title="t('activities.selectAnActivity')">
      <form
          class="form"
          novalidate
          @submit.prevent="handleSubmit"
      >
        <FormRow>
          <FormSelect
              v-model="selectedActivityId"
              :label="t('activities.activity')"
              :options="activitiesOptions"
              :rules="[]"
              name="activityId"
          />

          <FormSelect
              v-if="selectedActivityId"
              v-model="selectedActivityOccurrenceId"
              :label="t('activities.selectAnOccurrence')"
              :options="activityOccurrencesOptions"
              :rules="[]"
              name="activityOccurrenceId"
          />
        </FormRow>

        <div class="form__submit">
          <button
              :disabled="disableDownloadButton"
              class="btn btn--fullscreen"
              type="submit"
          >
            {{ t("participants.download") }}
          </button>
        </div>
      </form>
    </Card>
    <ActivityParticipantTable
        v-model:activity-id="selectedActivityId"
        v-model:activity-occurrence-id="selectedActivityOccurrenceId"
        v-model:isOnlineActivity="isOnlineActivity"
        v-model:max-participants="selectedActivityOccurrence.activity.maxParticipants"
        v-model:participants="participants"
        v-model:participantsAreLoading="participantsAreLoading"
        v-model:participantsSearchValue="participantsSearchValue"
        @new-participant-added="newParticipantAdded"/>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import Card from '@/components/layout/Card.vue';
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import BackLinkTitle from "@/components/layout/BackLinkTitle.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import FormRow from "@/components/forms/FormRow.vue";
import SearchInput from "@/components/layout/SearchInput.vue";
import ActivityParticipantTable from "@/components/tables/ActivityParticipantTable.vue";

import {FormOption, Guid} from "@/types";
import {useActivityOccurrenceService, useActivityService} from "@/inversify.config";
import {Activity, ActivityLocation, Participant} from "@/types/entities/activity";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import {ComputedRef} from "vue/dist/vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  activityId: string
  activityOccurrenceId: string
}>()

const {t} = useI18n()

const activityService = useActivityService()
const activityOccurrenceService = useActivityOccurrenceService()

const selectedActivityId = ref(props.activityId ?? "")
const activities = ref<Activity[]>(await activityService.getAllActivities(true) ?? [])
const selectedActivity = ref<Activity>(findActivityWithId(selectedActivityId.value))

const givenActivityOccurrenceId = ref(props.activityOccurrenceId ?? "")
const activityOccurrences = ref<ActivityOccurrence[]>(selectedActivity.value ? selectedActivity.value.activityOccurrences : [])
const selectedActivityOccurrence = ref<ActivityOccurrence>(activityOccurrences.value.length > 0 ? activityOccurrences.value[0] as ActivityOccurrence : new ActivityOccurrence())
const selectedActivityOccurrenceId = ref(Guid.isValid(givenActivityOccurrenceId.value) ? props.activityOccurrenceId : selectedActivityOccurrence.value.id ?? "")

const participants = ref<Participant[]>([]);
const participantsAreLoading = ref(false);
const participantsSearchValue = ref("");

onMounted(async () => {
  await fetchActivityOccurrenceParticipants()
})

const disableDownloadButton = computed(() => {
  return !selectedActivityId.value || !selectedActivityOccurrenceId.value || !participants.value.length
})

let activitiesOptions: ComputedRef<FormOption[]> = computed(() => {
  let formOptions = activities.value
      .filter(x => x && x.id && x.nameFr)
      .map(({id, nameFr}) => ({
        name: id,
        label: nameFr
      })) as FormOption[]
  return formOptions.sortByLabel()
});

let activityOccurrencesOptions: any = computed(() => {
  if (activityOccurrences.value.length === 0)
    return []
  return activityOccurrences.value
      .filter(x => x && x.id && x.startDate)
      .map(x => {
        let startDate = x.startDate ? x.startDate : ""
        let endDate = x.endDate ? ` ---> ${x.endDate}` : ''
        return {
          name: x.id,
          label: `${startDate}${endDate}`
        }
      });
});

let isOnlineActivity: any = computed(() => {
  if (!Guid.isValid(selectedActivityId.value))
    return false

  let activity = findActivityWithId(selectedActivityId.value)
  return activity?.location == ActivityLocation.Online
});

watch(selectedActivityId, (newId) => {
  participants.value = []
  selectedActivity.value = findActivityWithId(newId)

  activityOccurrences.value = selectedActivity.value.activityOccurrences
  if (activityOccurrences.value.length === 0)
    return
  selectedActivityOccurrence.value = activityOccurrences.value[0] as ActivityOccurrence
  selectedActivityOccurrenceId.value = activityOccurrences.value[0].id as string
})

watch(selectedActivityOccurrenceId, async () => {
  await fetchActivityOccurrenceParticipants()
})

function findActivityWithId(id: string): Activity {
  return activities.value.find(x => x.id == id) as Activity;
}

async function fetchActivityOccurrenceParticipants() {
  if (!selectedActivityOccurrenceId.value) return

  participantsAreLoading.value = true
  participants.value = await activityOccurrenceService.getActivityOccurrenceParticipants(selectedActivityOccurrenceId.value);
  participantsAreLoading.value = false
}

async function newParticipantAdded() {
  await fetchActivityOccurrenceParticipants()
}

async function handleSubmit() {
  let csvData = ",Num membre,Prenom,Nom,Email";
  if (!isOnlineActivity.value)
    csvData += ",Allergies,Mobilite reduite"
  for (let index = 0; index < participants.value.length; index++) {
    const p = participants.value[index];
    csvData += `\n${index + 1},${p.memberNumber?.length ? p.memberNumber : t("global.undefined")},${p.firstName},${p.lastName},${p.emailAddress}`;
    if (!isOnlineActivity.value) csvData += `,${p.allergies?.length ? p.allergies : t("global.undefined")},${p.reducedMobility ? t("global.yes") : t("global.no")}`
  }

  const utf8BOM = "\uFEFF"; // Optional but recommended for proper UTF-8 encoding
  let blob = new Blob([utf8BOM, csvData], {type: "text/csv;charset=utf-8;"});
  let url = URL.createObjectURL(blob);
  let link = document.createElement("a");
  link.setAttribute("href", url);

  let activity = activities.value.find(x => x.id == selectedActivityId.value)
  link.setAttribute("download", `${activity?.nameFr}_participants.csv`);

  // Simulate a click on the link to initiate the download
  link.click();
}
</script>
